import { create } from 'zustand';
import { PriceType, SupplierPriceListResponse } from '@api/generated/search-service';
import { supplierPricesApi } from '@api/api-config';
import { useOrgPortal } from '@hooks/useOrgPortal.ts';

type GuaranteedFilter = 'GUARANTEED' | 'NOT_GUARANTEED' | 'ALL';

interface SupplierPriceFilters {
  page?: number;
  size?: number;
  query?: string;
  organizationId?: number;
  auctionId?: number;
  guaranteedFilter?: GuaranteedFilter;
  priceType?: PriceType;
}

interface SupplierPriceState {
  prices: SupplierPriceListResponse[];
  total: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrev: boolean;
  isLoading: boolean;
  error: string | null;
  filters: SupplierPriceFilters;

  fetchPrices: (filters?: Partial<SupplierPriceFilters>) => Promise<void>;
  setFilters: (filters: Partial<SupplierPriceFilters>) => void;
  clearFilters: () => void;
  clearError: () => void;
}

const defaultFilters: SupplierPriceFilters = {
  page: 1,
  size: 20,
  guaranteedFilter: 'ALL',
};

export const useSupplierPriceStore = create<SupplierPriceState>(set => ({
  prices: [],
  total: 0,
  totalPages: 0,
  currentPage: 1,
  pageSize: 20,
  hasNext: false,
  hasPrev: false,
  isLoading: false,
  error: null,
  filters: defaultFilters,

  fetchPrices: async (newFilters?: Partial<SupplierPriceFilters>) => {
    set({ isLoading: true, error: null });

    try {
      const { orgId: supplierId, portalId } = useOrgPortal.getState();

      if (!supplierId || !portalId) {
        set({ error: 'Dodavatel nebo portál nebyl nalezen', isLoading: false });
        return;
      }

      if (newFilters) {
        set(state => ({
          filters: { ...state.filters, ...newFilters },
        }));
      }

      const currentState = useSupplierPriceStore.getState();
      const filters = { ...currentState.filters, ...newFilters };

      const response = await supplierPricesApi.getSupplierPrices({
        supplierId,
        portalId,
        page: filters.page,
        size: filters.size,
        query: filters.query,
        organizationId: filters.organizationId,
        auctionId: filters.auctionId,
        guaranteedFilter: filters.guaranteedFilter,
        priceType: filters.priceType,
      });

      set({
        prices: response.data?.items || [],
        total: response.data?.total || 0,
        totalPages: response.data?.totalPages || 0,
        currentPage: response.data?.page || 1,
        pageSize: response.data?.size || 20,
        hasNext: response.data?.hasNext || false,
        hasPrev: response.data?.hasPrev || false,
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se načíst ceny';
      set({
        error: errorMessage,
        isLoading: false,
      });
    }
  },

  setFilters: (newFilters: Partial<SupplierPriceFilters>) => {
    set(state => ({
      filters: { ...state.filters, ...newFilters, page: 1 },
    }));
  },

  clearFilters: () => {
    set({ filters: defaultFilters });
  },

  clearError: () => {
    set({ error: null });
  },
}));
